import UserManagementIcon from "../../../../../assets/svg/user-management-icon";
import CompletedIcon from "../../../../../assets/svg/completed-icon";
import TimeIcon from "../../../../../assets/svg/time-icon";
import InactiveUserIcon from "../../../../../assets/svg/inactive-user-icon";
import CrossIcon from "../../../../../assets/svg/cross-icon";
import { ActionConfig, AllUsersLayoutTab } from "./types";
import PreviewIcon from "../../../../../assets/svg/preview-icon";
import ResendShareIcon from "../../../../../assets/svg/resend-share-icon";
import ActiveUserIcon from "../../../../../assets/svg/active-user-icon";
import EditUserIcon from "../../../../../assets/svg/edit-user-icon";
import {
  clearAllCheckedUserIds,
  setAllCheckedUserIds,
  toggleCheckedUser,
} from "../../../../../redux/slices/userManagementSlice";
import { ColumnDef } from "@tanstack/react-table";
import { ROLE_CONFIG } from "../../RolesLayout/utils/constants";
import { getDateTimeByFormat } from "../../../../../redux/slices/dateTimeFormatSlice";
import AgentIcon from "../../../../../assets/svg/agent-icon";
import SortByIcon from "../../../../../assets/svg/sort-by-icon";
import { TruncatedTextTooltip } from "../../../../../components/TruncatedTextTooltip/TruncatedTextTooltip";
import { UserData } from "../utils/types";
import OrganizationDisplay from "../components/OrganizationDisplay";
import { AppDispatch } from "../../../../../redux/store";
import { Avatar } from "../../../../../components/Avatar/Avatar";
import DeleteIcon from "../../../../../assets/svg/delete-icon";

export const totalUsersTabsConfig: AllUsersLayoutTab[] = [
  {
    id: "total_users",
    label: "Total Users",
    bgColor:
      "bg-user-management-blue-card-bg dark:bg-user-management-blue-card-bg-dk",
    icon: {
      component: UserManagementIcon,
      className: "w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk",
    },
    gradientBg: "var(--color-user-management-blue-card-bg-gradient)",
  },
  {
    id: "active_users",
    label: "Active Users",
    bgColor: "bg-verba-green-success/10 dark:bg-verba-green-success-dk/10",
    icon: {
      component: CompletedIcon,
      className:
        "w-[18px] h-[18px] text-verba-green-success dark:text-verba-green-success-dk",
    },
    gradientBg: "var(--color-user-management-green-card-bg-gradient)",
  },
  {
    id: "pending_invites",
    label: "Pending Invites",
    bgColor: "bg-analytics-safety/10 dark:bg-analytics-safety-dk/10",
    icon: {
      component: TimeIcon,
      className:
        "w-[18px] h-[18px] text-analytics-safety dark:text-analytics-safety-dk",
    },
    isThreeDotsVisible: true,
    gradientBg: "var(--color-purple-card-bg-gradient)",
  },
  {
    id: "inactive_users",
    label: "Inactive Users",
    bgColor:
      "bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk",
    icon: {
      component: InactiveUserIcon,
      className:
        "w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk",
    },
    gradientBg: "var(--color-user-management-red-card-bg-gradient)",
  },
  {
    id: "expired_invites",
    label: "Expired",
    bgColor:
      "bg-user-management-red-card-bg dark:bg-user-management-red-card-bg-dk",
    icon: {
      component: CrossIcon,
      className:
        "w-[18px] h-[18px] text-verba-red-alert dark:text-verba-red-alert-dk",
    },
    isThreeDotsVisible: true,
    gradientBg: "var(--color-user-management-red-card-bg-gradient)",
  },
];

export const SORTABLE_FIELDS = ["user", "role", "created_at", "status"];

export const userManagementTabsConfig = (isMultiOrganization: boolean) => [
  {
    id: "all-users",
    label: "All Users",
  },
  {
    id: "roles",
    label: "Roles",
  },
  isMultiOrganization && {
    id: "org-users",
    label: "Org Users",
  },
];

export const STATUS_CONFIG = {
  active: {
    label: "Active",
    extraClass:
      "text-verba-green-success dark:text-verba-green-success-dk border border-verba-green-success dark:border-verba-green-success-dk",
  },
  inactive: {
    label: "Inactive",
    extraClass:
      "text-gray-placeholder dark:text-gray-placeholder-dk border border-gray-placeholder dark:border-gray-placeholder-dk",
  },
  pending: {
    label: "Pending Invite",
    extraClass:
      "text-analytics-safety dark:text-analytics-safety-dk border border-analytics-safety dark:border-analytics-safety-dk",
  },
  expired: {
    label: "Expired",
    extraClass:
      "text-verba-red-alert dark:text-verba-red-alert-dk border border-verba-red-alert dark:border-verba-red-alert-dk",
  },
};

export const STEP_TITLES = [
  {
    step: 1,
    title: "User Details",
  },
  {
    step: 2,
    title: "Assign Role",
  },
  {
    step: 3,
    title: "Agent Details",
  },
];

export const USER_ACTIONS_CONFIG: ActionConfig[] = [
  {
    id: "view",
    label: "View",
    icon: PreviewIcon,
    className:
      "text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk",
    iconClassName: "text-verba-indigo dark:text-verba-indigo-dk",
  },
  {
    id: "edit",
    label: "Edit User",
    icon: EditUserIcon,
    className:
      "text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk",
    iconClassName: "text-text-tag-scheduled dark:text-text-tag-scheduled-dk",
  },
  {
    id: "resend_invite",
    label: "Resend Invite",
    icon: ResendShareIcon,
    className:
      "text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk",
    iconClassName: "text-tag-scheduled dark:text-tag-scheduled-dk",
  },
  {
    id: "mark_active",
    label: "Mark Active",
    icon: ActiveUserIcon,
    className:
      "text-heading-text dark:text-heading-text-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk",
    iconClassName: "text-verba-blue dark:text-verba-blue-dk",
  },
  {
    id: "mark_inactive",
    label: "Mark Inactive",
    icon: InactiveUserIcon,
    className:
      "text-verba-red-alert dark:text-verba-red-alert-dk hover:bg-tag-red-bg dark:hover:bg-tag-red-bg-dk",
    iconClassName: "text-verba-red-alert dark:text-verba-red-alert-dk",
  },
  {
    id: "delete",
    label: "Delete User",
    icon: DeleteIcon,
    className:
      "text-verba-red-alert dark:text-verba-red-alert-dk hover:bg-tag-red-bg dark:hover:bg-tag-red-bg-dk",
    iconClassName: "text-verba-red-alert dark:text-verba-red-alert-dk",
  },
];

export const createdUserTableColumns = (
  selectedUserIds: string[],
  allUsers: UserData[],
  dispatch: AppDispatch,
): ColumnDef<UserData>[] => [
  {
    accessorKey: "checkbox",
    size: 40,
    header: () => (
      <div className="flex items-center justify-center h-full px-3">
        <input
          type="checkbox"
          className="w-[10.67px] h-[10.67px] rounded-[3.05px] transition-opacity duration-200 cursor-pointer"
          onChange={() => {
            if (
              allUsers?.length === selectedUserIds?.length &&
              allUsers?.length > 0
            ) {
              dispatch(clearAllCheckedUserIds());
            } else {
              dispatch(
                setAllCheckedUserIds(allUsers?.map((user) => user?.user?.id)),
              );
            }
          }}
          checked={
            allUsers?.length === selectedUserIds?.length && allUsers?.length > 0
          }
        />
      </div>
    ),
    cell: ({ row }) => {
      const rowData = row?.original;
      const isSelected = selectedUserIds?.includes(rowData?.user?.id);
      return (
        <div
          className={`flex items-center justify-center h-full px-3 ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          <input
            type="checkbox"
            className={`w-[10.67px] h-[10.67px] rounded-[3.05px] cursor-pointer ${
              isSelected ? "opacity-100" : "opacity-0"
            } group-hover/row:opacity-100 transition-opacity duration-200`}
            onChange={() => {
              dispatch(toggleCheckedUser(rowData?.user?.id));
            }}
            checked={isSelected}
          />
        </div>
      );
    },
  },
  {
    accessorKey: "user",
    minSize: 350,
    header: () => (
      <div className="flex items-center gap-1 py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          User Details
        </p>
        <SortByIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
      </div>
    ),
    cell: ({ row }) => {
      const user = row?.getValue("user") as UserData["user"];
      const userId = row?.original?.user?.id;
      const isSelected = selectedUserIds?.includes(userId);
      return (
        <div
          className={`flex items-center flex-wrap gap-2 px-3 h-full overflow-hidden ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          <Avatar text={user?.name} size={24} className="shrink-0" />
          <div className="flex flex-col gap-y-0.5 overflow-hidden">
            <TruncatedTextTooltip
              text={user?.name}
              maxWidth="max-w-full text-md font-medium text-verba-indigo dark:text-verba-indigo-dk leading-tight"
            >
              {user?.name}
            </TruncatedTextTooltip>
            <div className="flex items-center flex-wrap gap-2 overflow-hidden">
              <TruncatedTextTooltip
                text={user?.email}
                maxWidth="max-w-[160px] text-xs font-normal text-data dark:text-data-dk leading-tight"
              >
                {user?.email}
              </TruncatedTextTooltip>
              <div className="w-px h-2.5 bg-border-gray dark:bg-border-gray-dk shrink-0" />
              <TruncatedTextTooltip
                text={user?.phone}
                maxWidth="max-w-[120px] text-xs font-normal text-data dark:text-data-dk leading-tight"
              >
                {user?.phone}
              </TruncatedTextTooltip>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "role",
    size: 130,
    cell: ({ row }) => {
      const roles = row?.getValue("role") as UserData["role"];
      const roleBgColor =
        ROLE_CONFIG[roles?.value as keyof typeof ROLE_CONFIG]?.bgColor;
      const userId = row?.original?.user?.id;
      const isSelected = selectedUserIds?.includes(userId);
      return (
        <div
          className={`flex items-center px-3 h-full ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          <div className="flex items-center gap-[5px] w-max border border-border-gray dark:border-border-gray-dk rounded-md px-1.5 py-0.5 shadow-xs-blur">
            <span className={`w-2 h-2 rounded-full shrink-0 ${roleBgColor}`} />
            <span className="text-left text-sm font-medium text-verba-indigo dark:text-verba-indigo-dk truncate">
              {roles?.label}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "created_by",
    size: 160,
    header: () => (
      <div className="py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Created By
        </p>
      </div>
    ),
    cell: ({ row }) => {
      const createdBy = row?.getValue("created_by") as UserData["created_by"];
      const userId = row?.original?.user?.id;
      const isSelected = selectedUserIds?.includes(userId);
      return (
        <div
          className={`flex items-center px-3 text-left h-full overflow-hidden ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          {createdBy ? (
            <span className="text-sm text-data dark:text-data-dk truncate">
              {createdBy}
            </span>
          ) : (
            <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              -
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    size: 150,
    header: () => (
      <div className="flex items-center gap-1 py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Created On
        </p>
        <SortByIcon className="w-[18px] h-[18px] text-verba-blue dark:text-verba-blue-dk" />
      </div>
    ),
    cell: ({ row }) => {
      const dateTime = getDateTimeByFormat(
        new Date(row?.getValue("created_at")),
      );
      const userId = row?.original?.user?.id;
      const isSelected = selectedUserIds?.includes(userId);
      return (
        <div
          className={`px-3 flex flex-col justify-center text-xs gap-y-1 h-full ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          <span className="text-heading-text dark:text-heading-text-dk truncate">
            {dateTime?.date}
          </span>
          <span className="text-dark-gray dark:text-dark-gray-dk truncate">
            {dateTime?.time}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 130,
    cell: ({ row }) => {
      const status = row?.getValue("status") as UserData["status"];
      const userId = row?.original?.user?.id;
      const isSelected = selectedUserIds?.includes(userId);
      return (
        <div
          className={`flex items-center px-3 h-full ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          <span
            className={`px-2 text-sm py-[1.25px] font-medium rounded-sm whitespace-nowrap ${
              STATUS_CONFIG[status?.value as keyof typeof STATUS_CONFIG]
                ?.extraClass
            }`}
          >
            {status?.label}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "is_agent",
    size: 120,
    header: () => (
      <div className="flex items-center gap-1 py-2 px-3">
        <p className="text-left text-md font-medium text-gray-375 dark:text-gray-375-dk">
          Sales Agent
        </p>
      </div>
    ),
    cell: ({ row }) => {
      const isAgent = row?.getValue("is_agent") as UserData["is_agent"];
      const userId = row?.original?.user?.id;
      const isSelected = selectedUserIds?.includes(userId);
      return (
        <div
          className={`flex items-center px-3 h-full ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          {isAgent ? (
            <div className="px-2 flex items-center gap-0.5 w-max border border-border-gray dark:border-border-gray-dk rounded-md py-0.5 shadow-xs-blur">
              <AgentIcon className="w-[13px] h-[13px] text-lead-stage-contacted dark:text-lead-stage-contacted-dk" />
              <span className="text-verba-indigo dark:text-verba-indigo-dk text-sm">
                Agent
              </span>
            </div>
          ) : (
            <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              No
            </p>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "clients",
    minSize: 200,
    cell: ({ row }) => {
      const organization = row?.getValue("clients") as UserData["clients"];
      const userId = row?.original?.user?.id;
      const isSelected = selectedUserIds?.includes(userId);
      return (
        <div
          className={`flex items-center px-3 h-full overflow-hidden ${
            isSelected ? "bg-card-highlight dark:bg-card-highlight-dk" : ""
          }`}
        >
          <OrganizationDisplay
            organizations={organization}
            containerWidth={350}
          />
        </div>
      );
    },
  },
];
